import { cacheVersionService } from './cacheVersionService';
import { persistentContentCache } from './persistentContentCacheService';
import { contentCacheService } from './contentCacheService';
import { episodesCacheService } from './episodesCacheService';
import { seriesMetadataCacheService } from './seriesMetadataCacheService';
import { emitCacheInvalidated } from '@/utils/cacheEvents';
import { ContentService } from './contentService';
import { Serie } from '@/types';

interface InvalidationResult {
  success: boolean;
  reason: string;
  cachesClearedCount: number;
  dataRefreshed: boolean;
  episodesPrefetched?: number;
  seriesPrefetched?: number;
  error?: string;
}

interface RefreshProgress {
  step: string;
  progress: number;
  total: number;
  message: string;
}

interface EpisodesPrefetchStats {
  totalSeries: number;
  processedSeries: number;
  totalEpisodes: number;
  successfulSeries: number;
  failedSeries: number;
  durationMs: number;
}

type ProgressCallback = (progress: RefreshProgress) => void;

class CacheInvalidationService {
  private readonly PREFETCH_BATCH_SIZE = 5;
  private readonly PREFETCH_ENABLED = true;

  private async prefetchAllEpisodes(
    campaignId: string,
    series: Serie[],
    onProgress?: ProgressCallback
  ): Promise<EpisodesPrefetchStats> {
    const startTime = Date.now();

    const stats: EpisodesPrefetchStats = {
      totalSeries: series.length,
      processedSeries: 0,
      totalEpisodes: 0,
      successfulSeries: 0,
      failedSeries: 0,
      durationMs: 0,
    };

    /*console.log('🎬 [EPISODES-PREFETCH] ========================================');
    console.log('🎬 [EPISODES-PREFETCH] Starting automatic episodes prefetch');
    console.log(`🎬 [EPISODES-PREFETCH]   - Campaign: ${campaignId}`);
    console.log(`🎬 [EPISODES-PREFETCH]   - Total series: ${series.length}`);
    console.log(`🎬 [EPISODES-PREFETCH]   - Batch size: ${this.PREFETCH_BATCH_SIZE}`);
    console.log('🎬 [EPISODES-PREFETCH] ========================================');*/

    if (series.length === 0) {
      //console.log('🎬 [EPISODES-PREFETCH] ⚠️ No series to prefetch');
      return stats;
    }

    const batches: Serie[][] = [];
    for (let i = 0; i < series.length; i += this.PREFETCH_BATCH_SIZE) {
      batches.push(series.slice(i, i + this.PREFETCH_BATCH_SIZE));
    }

    //console.log(`🎬 [EPISODES-PREFETCH] Processing ${batches.length} batches...`);

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const batchNumber = batchIndex + 1;

      //console.log(`🎬 [EPISODES-PREFETCH] Batch ${batchNumber}/${batches.length}: Processing ${batch.length} series...`);

      const batchPromises = batch.map(async (serie) => {
        try {
          const seriesId = serie.id;
          //console.log(`🎬 [EPISODES-PREFETCH]   → Fetching episodes for "${serie.title}" (ID: ${seriesId})...`);
          //console.log(`🎬 [EPISODES-PREFETCH]   → Using campaignId: ${campaignId}`);

          const episodes = await ContentService.getAllEpisodesForSeries(campaignId, seriesId);
          //console.log(`🎬 [EPISODES-PREFETCH]   → Received ${episodes?.length || 0} episodes from ContentService`);

          if (episodes && episodes.length > 0) {
            stats.totalEpisodes += episodes.length;
            stats.successfulSeries++;
            //console.log(`🎬 [EPISODES-PREFETCH]   ✅ "${serie.title}" - ${episodes.length} episodes will be cached by ContentService`);
          } else {
            ;//console.log(`🎬 [EPISODES-PREFETCH]   ⚠️ "${serie.title}" - No episodes returned from ContentService`);
          }

          stats.processedSeries++;

          onProgress?.({
            step: 'prefetching_episodes',
            progress: stats.processedSeries,
            total: stats.totalSeries,
            message: `Prefetching episodes (${stats.processedSeries}/${stats.totalSeries} series)`,
          });
        } catch (error) {
          stats.failedSeries++;
          stats.processedSeries++;
          console.error(`🎬 [EPISODES-PREFETCH]   ❌ Failed to prefetch "${serie.title}":`, error);
        }
      });

      await Promise.all(batchPromises);

      const progress = Math.round((stats.processedSeries / stats.totalSeries) * 100);
      //console.log(`🎬 [EPISODES-PREFETCH] Batch ${batchNumber}/${batches.length} complete - Progress: ${stats.processedSeries}/${stats.totalSeries} (${progress}%)`);
    }

    stats.durationMs = Date.now() - startTime;
    const durationSeconds = Math.round(stats.durationMs / 1000);

    console.log('🎬 [EPISODES-PREFETCH] ========================================');
    console.log('🎬 [EPISODES-PREFETCH] ✅ Episodes prefetch COMPLETE');
    console.log(`🎬 [EPISODES-PREFETCH]   - Series processed: ${stats.processedSeries}/${stats.totalSeries}`);
    console.log(`🎬 [EPISODES-PREFETCH]   - Successful: ${stats.successfulSeries}`);
    console.log(`🎬 [EPISODES-PREFETCH]   - Failed: ${stats.failedSeries}`);
    console.log(`🎬 [EPISODES-PREFETCH]   - Total episodes cached: ${stats.totalEpisodes}`);
    console.log(`🎬 [EPISODES-PREFETCH]   - Duration: ${durationSeconds} seconds`);
    console.log('🎬 [EPISODES-PREFETCH] ========================================');

    return stats;
  }

  async checkAndInvalidateIfNeeded(
    onProgress?: ProgressCallback,
    forceCheck?: boolean
  ): Promise<InvalidationResult> {
    const startTime = Date.now();
    //console.log('🔄 [CACHE-INVALIDATION] ========================================');
    //console.log(`🔄 [CACHE-INVALIDATION] Checking global cache validity... (forceCheck: ${!!forceCheck})`);

    try {
      const versionCheck = await cacheVersionService.shouldInvalidateCache(forceCheck);

      if (!versionCheck.shouldInvalidate) {
        const duration = Date.now() - startTime;
        //console.log(`🔄 [CACHE-INVALIDATION] ✅ Cache is valid, no action needed (${duration}ms)`);

        if (versionCheck.reason === 'first_run' && versionCheck.remoteVersion) {
          await cacheVersionService.setLocalVersion(versionCheck.remoteVersion);
          //console.log('🔄 [CACHE-INVALIDATION] 💾 Saved remote version for first run');
        }

        //console.log('🔄 [CACHE-INVALIDATION] ========================================');

        return {
          success: true,
          reason: versionCheck.reason,
          cachesClearedCount: 0,
          dataRefreshed: false,
        };
      }

      if (versionCheck.reason === 'cache_empty') {
        console.log('🔄 [CACHE-INVALIDATION] ⚠️ Cache is empty, forcing refresh from Supabase...');
      } else if (versionCheck.reason === 'lost_version_tracking') {
        console.log('🔄 [CACHE-INVALIDATION] ⚠️ Lost version tracking detected, forcing refresh...');
      } else {
        console.log('🔄 [CACHE-INVALIDATION] ❌ Cache is outdated, starting invalidation...');
      }

      const result = await this.invalidateAndRefreshCache(onProgress);

      if (result.success) {
        await cacheVersionService.setLocalVersion(versionCheck.remoteVersion!);
        console.log('🔄 [CACHE-INVALIDATION] ✅ Local version updated to match remote');
      }

      const duration = Date.now() - startTime;
      console.log(`🔄 [CACHE-INVALIDATION] Total duration: ${duration}ms`);
      console.log('🔄 [CACHE-INVALIDATION] ========================================');

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`🔄 [CACHE-INVALIDATION] Error during invalidation check (${duration}ms):`, error);
      console.log('🔄 [CACHE-INVALIDATION] ========================================');

      return {
        success: false,
        reason: 'error',
        cachesClearedCount: 0,
        dataRefreshed: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async invalidateAndRefreshCache(
    onProgress?: ProgressCallback
  ): Promise<InvalidationResult> {
    const startTime = Date.now();
    let cachesClearedCount = 0;

    //console.log('🔄 [CACHE-INVALIDATION] ========================================');
    //console.log('🔄 [CACHE-INVALIDATION] Starting global cache invalidation and refresh...');
    //console.log('🔄 [CACHE-INVALIDATION] This will clear all caches and fetch fresh data from Supabase');

    try {
      // PHASE 1: Capture series list BEFORE clearing cache
      //console.log('🔄 [CACHE-INVALIDATION] Phase 1: Capturing existing series list...');
      const campaignId = persistentContentCache.getCurrentCampaignId();
      //console.log(`🔄 [CACHE-INVALIDATION] Current campaign ID: ${campaignId || 'NULL'}`);

      let seriesForPrefetch = null;

      if (this.PREFETCH_ENABLED && campaignId) {
        seriesForPrefetch = await persistentContentCache.getAllSeries();
        if (seriesForPrefetch && seriesForPrefetch.length > 0) {
          //console.log(`🔄 [CACHE-INVALIDATION] ✅ Captured ${seriesForPrefetch.length} series for episode prefetch`);
          ;//console.log(`🔄 [CACHE-INVALIDATION] First series example: ${seriesForPrefetch[0]?.title || 'NO TITLE'} (ID: ${seriesForPrefetch[0]?.id})`);
        } else {
          ;//console.log('🔄 [CACHE-INVALIDATION] ⚠️ No series found in cache (first run or empty cache)');
        }
      } else if (!campaignId) {
        ;//console.log('🔄 [CACHE-INVALIDATION] ⚠️ No campaign ID available, cannot capture series');
      } else if (!this.PREFETCH_ENABLED) {
        ;//console.log('🔄 [CACHE-INVALIDATION] ℹ️ Episodes prefetch is DISABLED');
      }

      // PHASE 2: Clear all caches
      //console.log('🔄 [CACHE-INVALIDATION] Phase 2: Clearing all caches...');

      onProgress?.({
        step: 'clearing',
        progress: 0,
        total: 4,
        message: 'Clearing persistent content cache...',
      });

      //console.log('🔄 [CACHE-INVALIDATION] Step 1/4: Clearing persistent content cache...');
      await persistentContentCache.clear();
      cachesClearedCount++;

      onProgress?.({
        step: 'clearing',
        progress: 1,
        total: 4,
        message: 'Clearing content cache...',
      });

      //console.log('🔄 [CACHE-INVALIDATION] Step 2/4: Clearing content cache...');
      await contentCacheService.clear();
      cachesClearedCount++;

      onProgress?.({
        step: 'clearing',
        progress: 2,
        total: 4,
        message: 'Clearing episodes cache...',
      });

      //console.log('🔄 [CACHE-INVALIDATION] Step 3/4: Clearing episodes cache...');
      await episodesCacheService.clear();
      cachesClearedCount++;

      onProgress?.({
        step: 'clearing',
        progress: 3,
        total: 4,
        message: 'Clearing series metadata cache...',
      });

      //console.log('🔄 [CACHE-INVALIDATION] Step 4/4: Clearing series metadata cache...');
      await seriesMetadataCacheService.clear();
      cachesClearedCount++;

      const duration = Date.now() - startTime;
      /*console.log('🔄 [CACHE-INVALIDATION] ========================================');
      console.log(`🔄 [CACHE-INVALIDATION] ✅ Cache clearing COMPLETE (${duration}ms)`);
      console.log(`🔄 [CACHE-INVALIDATION]   - Caches cleared: ${cachesClearedCount}`);
      console.log('🔄 [CACHE-INVALIDATION] ========================================');*/

      // PHASE 3: Broadcast invalidation event
      //console.log('🔄 [CACHE-INVALIDATION] Phase 3: Broadcasting cache invalidation event...');
      //console.log('🔄 [CACHE-INVALIDATION] 📡 Broadcasting cache invalidation event to all listeners...');
      emitCacheInvalidated();
      //console.log('🔄 [CACHE-INVALIDATION] ✅ Event broadcasted successfully');

      onProgress?.({
        step: 'waiting_for_data',
        progress: 4,
        total: 5,
        message: 'Waiting for series data to reload...',
      });

      //console.log('🔄 [CACHE-INVALIDATION] ⏳ Waiting for series data to be reloaded by components...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // PHASE 4: Prefetch episodes
      let prefetchStats: EpisodesPrefetchStats | null = null;

      if (this.PREFETCH_ENABLED) {
        //console.log('🔄 [CACHE-INVALIDATION] Phase 4: Prefetching episodes...');

        if (!campaignId) {
          ;//console.log('🔄 [CACHE-INVALIDATION] ⚠️ No campaign ID available, skipping episodes prefetch');
        } else {
          // Use captured series list OR fetch new one if it was empty
          let seriesToPrefetch = seriesForPrefetch;

          if (!seriesToPrefetch || seriesToPrefetch.length === 0) {
            //console.log('🔄 [CACHE-INVALIDATION] 📋 No series captured before clearing, fetching reloaded series...');
            seriesToPrefetch = await persistentContentCache.getAllSeries();
          } else {
            ;//console.log(`🔄 [CACHE-INVALIDATION] 📋 Using ${seriesToPrefetch.length} series captured before clearing`);
          }

          if (seriesToPrefetch && seriesToPrefetch.length > 0) {
            //console.log(`🔄 [CACHE-INVALIDATION] Starting episodes prefetch for ${seriesToPrefetch.length} series...`);

            prefetchStats = await this.prefetchAllEpisodes(campaignId, seriesToPrefetch, onProgress);
          } else {
            ;//console.log('🔄 [CACHE-INVALIDATION] ⚠️ No series available for episodes prefetch (cache still empty)');
          }
        }
      } else {
        ;//console.log('🔄 [CACHE-INVALIDATION] Episodes prefetch is DISABLED');
      }

      onProgress?.({
        step: 'complete',
        progress: 5,
        total: 5,
        message: 'Cache refresh complete!',
      });

      const totalDuration = Date.now() - startTime;
      //console.log('🔄 [CACHE-INVALIDATION] ========================================');
      //console.log(`🔄 [CACHE-INVALIDATION] ✅ FULL CACHE REFRESH COMPLETE (${totalDuration}ms)`);
      //console.log(`🔄 [CACHE-INVALIDATION]   - Caches cleared: ${cachesClearedCount}`);
      if (prefetchStats) {
        //console.log(`🔄 [CACHE-INVALIDATION]   - Episodes prefetched: ${prefetchStats.totalEpisodes}`);
        ;//console.log(`🔄 [CACHE-INVALIDATION]   - Series prefetched: ${prefetchStats.successfulSeries}/${prefetchStats.totalSeries}`);
      }
      //console.log('🔄 [CACHE-INVALIDATION] ========================================');

      return {
        success: true,
        reason: 'cache_refreshed',
        cachesClearedCount,
        dataRefreshed: true,
        episodesPrefetched: prefetchStats?.totalEpisodes || 0,
        seriesPrefetched: prefetchStats?.successfulSeries || 0,
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`🔄 [CACHE-INVALIDATION] ❌ Error during invalidation (${duration}ms):`, error);
      //console.log('🔄 [CACHE-INVALIDATION] ========================================');

      return {
        success: false,
        reason: 'error',
        cachesClearedCount,
        dataRefreshed: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async forceClearAllCaches(): Promise<InvalidationResult> {
    const startTime = Date.now();
    let cachesClearedCount = 0;

    //console.log('🔄 [CACHE-INVALIDATION] ========================================');
    //console.log('🔄 [CACHE-INVALIDATION] FORCE clearing ALL caches...');

    try {
      await persistentContentCache.clear();
      cachesClearedCount++;

      await contentCacheService.clear();
      cachesClearedCount++;

      await episodesCacheService.clear();
      cachesClearedCount++;

      await seriesMetadataCacheService.clear();
      cachesClearedCount++;

      await cacheVersionService.clearLocalVersion();

      const duration = Date.now() - startTime;
      //console.log(`🔄 [CACHE-INVALIDATION] ✅ Force clear complete: ${cachesClearedCount} caches cleared (${duration}ms)`);
      //console.log('🔄 [CACHE-INVALIDATION] ========================================');

      //console.log('🔄 [CACHE-INVALIDATION] 📡 Broadcasting cache invalidation event to all listeners...');
      emitCacheInvalidated();
      //console.log('🔄 [CACHE-INVALIDATION] ✅ Event broadcasted successfully');

      return {
        success: true,
        reason: 'force_cleared',
        cachesClearedCount,
        dataRefreshed: false,
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`🔄 [CACHE-INVALIDATION] ❌ Error during force clear (${duration}ms):`, error);
      //console.log('🔄 [CACHE-INVALIDATION] ========================================');

      return {
        success: false,
        reason: 'error',
        cachesClearedCount,
        dataRefreshed: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async forceClearAndRefresh(
    onProgress?: ProgressCallback
  ): Promise<InvalidationResult> {
    //console.log('🔄 [CACHE-INVALIDATION] ========================================');
    //console.log('🔄 [CACHE-INVALIDATION] FORCE clear and refresh global cache...');

    await this.forceClearAllCaches();

    const result = await this.invalidateAndRefreshCache(onProgress);

    if (result.success) {
      const remoteVersion = await cacheVersionService.getRemoteVersion();
      if (remoteVersion) {
        await cacheVersionService.setLocalVersion(remoteVersion);
      }
    }

    return result;
  }

  async getCacheInvalidationStats(): Promise<{
    hasLocalVersion: boolean;
    localVersion: string | null;
    age: number | null;
    needsInvalidation: boolean;
  }> {
    //console.log('🔄 [CACHE-INVALIDATION] Getting cache invalidation stats...');

    try {
      const stats = await cacheVersionService.getCacheStats();
      const versionCheck = await cacheVersionService.shouldInvalidateCache();

      return {
        ...stats,
        needsInvalidation: versionCheck.shouldInvalidate,
      };
    } catch (error) {
      console.error('🔄 [CACHE-INVALIDATION] Error getting stats:', error);
      return {
        hasLocalVersion: false,
        localVersion: null,
        age: null,
        needsInvalidation: false,
      };
    }
  }
}

export const cacheInvalidationService = new CacheInvalidationService();
export type { InvalidationResult, RefreshProgress };
